import CheckIcon from "@mui/icons-material/CheckRounded";
import { Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import useMergedSx from "@entrilia/shared/hooks/useMergedSx";
import type { ObjectAccessCategory } from "../../../api/types/objectTypes";
import { getCheckedGridSx } from "../../common/grid/gridStyles";
import PortalRoleChip from "../../common/PortalRoleChip";
import type { GridActions } from "./userAccessMatrixGridDataProvider";
import { ActionsCell, RegistrationStatusCell } from "./userAccessMatrixGridDataProvider";
import UserAccessMatrixGridToolbar from "./UserAccessMatrixGridToolbar";
import type { UserAccessMatrixRow } from "./userAccessMatrixModel";

const getCategoryColumnDefinition = (
  category: ObjectAccessCategory,
  isLast: boolean
): GridColDef<UserAccessMatrixRow> => ({
  field: category.id,
  headerName: category.name,
  minWidth: 160,
  flex: isLast ? 1 : undefined,
  renderCell: ({ row }) => (row.categories.includes(category.id) ? <CheckIcon color="primary" /> : null),
});

const getColumnDefinitions = (
  categories: ObjectAccessCategory[],
  actions: GridActions,
  hasPermissionsToManage: boolean
): GridColDef<UserAccessMatrixRow>[] => [
  {
    field: "name",
    headerName: "Name",
    width: 320,
    renderCell: ({ row }) => (
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <Typography variant="subtitle2">{row.name}</Typography>
        {row.email && (
          <Typography variant="caption" color="textSecondary">
            {row.email}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    field: "role",
    headerName: "Role",
    width: 160,
    renderCell: ({ row }) => <PortalRoleChip role={row.role} />,
  },
  {
    field: "registrationDate",
    headerName: "Registration Status",
    width: 160,
    headerClassName: "with-right-border",
    cellClassName: "with-right-border",
    renderCell: ({ row }) => <RegistrationStatusCell row={row} />,
  },
  ...categories.map((category, index) => getCategoryColumnDefinition(category, index === categories.length - 1)),
  {
    field: "actions",
    headerName: "Actions",
    resizable: false,
    align: "right",
    headerAlign: "right",
    width: 80,
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => (
      <ActionsCell row={row} actions={actions} hasPermissionsToManage={hasPermissionsToManage} />
    ),
  },
];

interface Props {
  rows: UserAccessMatrixRow[];
  categories: ObjectAccessCategory[];
  isLoading?: boolean;
  actions: GridActions;
  hasPermissionsToManage: boolean;
  excludeColumns?: string[];
}

const UserAccessMatrixGrid = ({
  rows,
  categories,
  isLoading,
  actions,
  hasPermissionsToManage,
  excludeColumns,
}: Props) => {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const columns = useMemo(
    () => getColumnDefinitions(categories, actions, hasPermissionsToManage),
    [categories, actions, hasPermissionsToManage]
  );

  const visibilityModel = useMemo(
    () => Object.fromEntries((excludeColumns ?? []).map((col) => [col, false])),
    [excludeColumns]
  );

  const showToolbar = useMemo(() => selectedRowIds.length > 0, [selectedRowIds.length]);

  const gridSx = useMergedSx(
    (t) => getCheckedGridSx(t),
    (t) => ({
      ".MuiDataGrid-columnHeaders": {
        ".MuiDataGrid-columnHeader": {
          "&.with-right-border": {
            borderRight: `1px solid ${t.palette.divider}`,
          },
        },
      },
      ".MuiDataGrid-row": {
        ".MuiDataGrid-cell": {
          "&.with-right-border": {
            borderRight: `1px solid ${t.palette.divider}`,
          },
        },
      },
    })
  );

  const handleEditSelectedUsers = (ids: string[]) => {
    actions.onEditUsers(rows.filter((row) => ids.includes(row.id)));
  };

  const handleRemoveSelectedUsers = (ids: string[]) => {
    actions.onRemoveUsers(rows.filter((row) => ids.includes(row.id)));
  };

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedRowIds, setSelectedRowIds]}
      actions={
        <UserAccessMatrixGridToolbar
          selectedIds={selectedRowIds}
          onEditSelectedUsers={handleEditSelectedUsers}
          onRemoveSelectedUsers={handleRemoveSelectedUsers}
        />
      }
    >
      <DataGrid<UserAccessMatrixRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_FIELD, "name"] }}
        noRowsText="No users"
        multilineRows
        checkboxSelection={hasPermissionsToManage}
        rowSelectionModel={getRowSelectionModelFromIds(selectedRowIds)}
        onRowSelectionModelChange={(selection) => setSelectedRowIds(getSelectedRowIdsFromModel(selection))}
        disableColumnReorder
        disableColumnSorting
        columnVisibilityModel={visibilityModel}
        sx={gridSx}
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default UserAccessMatrixGrid;
